import { Send, Square } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { API_URL } from "../../config/api";
import { useTranslation } from "../../hooks/useTranslation";

type Message = {
  id: string;
  role: "user" | "assistant" | "tool_call";
  content: string;
  timestamp: Date;
  toolName?: string;
  isToolCall?: boolean;
};

type Plugin = {
  id: string;
  name: string;
  description: string;
  active: boolean;
};

export const ChatInterface = () => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hintRef = useRef<HTMLDivElement>(null);

  // 使用 useMemo 来缓存插件列表，并在语言变化时更新
  const plugins = useMemo(
    () => [
      { id: "text", name: t("plugins.text.title"), description: t("plugins.text.description") },
      { id: "image", name: t("plugins.image.title"), description: t("plugins.image.description") },
      { id: "news", name: t("plugins.news.title"), description: t("plugins.news.description") },
      { id: "weather", name: t("plugins.weather.title"), description: t("plugins.weather.description") },
      { id: "education", name: t("plugins.education.title"), description: t("plugins.education.description") },
      { id: "finance", name: t("plugins.finance.title"), description: t("plugins.finance.description") },
      { id: "business", name: t("plugins.business.title"), description: t("plugins.business.description") },
      { id: "location", name: t("plugins.location.title"), description: t("plugins.location.description") },
      { id: "webtools", name: t("plugins.webtools.title"), description: t("plugins.webtools.description") },
      { id: "ai", name: t("plugins.ai.title"), description: t("plugins.ai.description") },
      { id: "barcode", name: t("plugins.barcode.title"), description: t("plugins.barcode.description") },
      { id: "lunar", name: t("plugins.lunar.title"), description: t("plugins.lunar.description") },
    ],
    [t]
  );

  // 初始化激活的插件
  const [activePlugins, setActivePlugins] = useState<Set<string>>(new Set(["education", "finance", "lunar"]));

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-adjust textarea height
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [message]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: message,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setIsTyping(true);

    // 创建助手消息的ID，但暂时不添加消息
    const assistantMessageId = (Date.now() + 1).toString();
    let accumulatedContent = "";

    try {
      const response = await fetch(`${API_URL}/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: message,
          variables: {},
          plugins: Array.from(activePlugins),
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No reader available");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let assistantMessage: Message | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        buffer += chunk;

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const jsonStr = line.slice(6);
              const data = JSON.parse(jsonStr);
              if (data.content) {
                const toolMatch = data.content.match(/<tool>(.*?)<\/tool>/);
                if (toolMatch) {
                  const toolName = toolMatch[1];
                  // 如果还没有创建助手消息，先创建一个
                  if (!assistantMessage) {
                    assistantMessage = {
                      id: assistantMessageId,
                      role: "assistant",
                      content: "",
                      timestamp: new Date(),
                      toolName,
                      isToolCall: true,
                    };
                    setMessages((prev) => [...prev, assistantMessage]);
                  } else {
                    // 更新现有助手消息的工具调用标记
                    setMessages((prev) => prev.map((msg) => (msg.id === assistantMessageId ? { ...msg, toolName, isToolCall: true } : msg)));
                  }
                } else {
                  // 普通内容
                  accumulatedContent += data.content;
                  if (!assistantMessage) {
                    // 如果是第一条内容，创建新的助手消息
                    assistantMessage = {
                      id: assistantMessageId,
                      role: "assistant",
                      content: accumulatedContent.trim(),
                      timestamp: new Date(),
                    };
                    setMessages((prev) => [...prev, assistantMessage]);
                  } else {
                    // 更新现有助手消息的内容
                    setMessages((prev) => prev.map((msg) => (msg.id === assistantMessageId ? { ...msg, content: accumulatedContent.trim() } : msg)));
                  }
                }
              }
            } catch (e) {
              console.error("Error parsing SSE data:", e);
            }
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Request was aborted");
        return;
      }
      console.error("Error:", error);
      // 只在有错误时才添加错误消息
      setMessages((prev) => [
        ...prev,
        {
          id: assistantMessageId,
          role: "assistant",
          content: "Sorry, there was an error processing your request.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsTyping(false);
      abortControllerRef.current = null;
    }
  };

  const handleExampleClick = (example: string) => {
    setMessage(example);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const togglePlugin = (pluginId: string) => {
    if (activePlugins.has(pluginId)) {
      setActivePlugins((prev) => {
        const next = new Set(prev);
        next.delete(pluginId);
        return next;
      });
    } else {
      // 检查是否是默认激活的插件
      if (["education", "finance", "lunar"].includes(pluginId)) {
        setActivePlugins((prev) => new Set([...prev, pluginId]));
      } else {
        // 显示提示
        setShowHint(true);
        // 5秒后隐藏提示
        setTimeout(() => {
          setShowHint(false);
        }, 5000);
      }
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsTyping(false);
      abortControllerRef.current = null;
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto" id="chat">
      {/* Chat window */}
      <div className="glass-panel h-[600px] md:h-[700px] flex flex-col">
        {/* Plugin toggles */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("plugins.title")}</h3>
            <div
              ref={hintRef}
              className={`flex items-center gap-2 text-sm text-orange-600/90 dark:text-orange-400/90 transition-all duration-300 ease-in-out overflow-hidden ${
                showHint ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full"
              }`}
            >
              <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="whitespace-nowrap">{t("plugins.coming_soon.description")}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {plugins.map((plugin) => (
              <button
                key={plugin.id}
                onClick={() => togglePlugin(plugin.id)}
                className={`px-3 py-1.5 text-xs rounded-full transition-all duration-300 flex items-center gap-1.5
                          ${activePlugins.has(plugin.id) ? "bg-brand-blue text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"}`}
                title={plugin.description}
              >
                <span className={`inline-block w-2 h-2 rounded-full ${activePlugins.has(plugin.id) ? "bg-green-400" : "bg-gray-400"}`}></span>
                <span>{plugin.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Messages container */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">{t("chat.title")}</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm whitespace-nowrap overflow-hidden text-ellipsis">{t("chat.description")}</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} mb-6`}>
                  <div className="relative group">
                    {msg.role === "assistant" && msg.isToolCall && (
                      <div className="flex items-center gap-2 mb-2">
                        <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-blue-500/50 text-blue-300">{msg.toolName}</div>
                        <span className="text-[10px] text-gray-400"></span>
                      </div>
                    )}
                    <div className={`inline-block rounded-2xl px-4 py-2.5 ${msg.role === "user" ? "bg-[#0B93F6] text-white" : "bg-[#F1F0F0] dark:bg-gray-700 text-gray-700 dark:text-gray-200"}`}>
                      <div className="prose dark:prose-invert max-w-none">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    </div>
                    <div className={`mt-1 text-center ${msg.role === "user" ? "text-right" : "text-left"}`}>
                      <span className="text-[10px] text-gray-400">{msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl px-4 py-3">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-pulse"></div>
                    <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-pulse" style={{ animationDelay: "0.2s" }}></div>
                    <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-pulse" style={{ animationDelay: "0.4s" }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Example queries */}
        <div className="px-4 pb-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{t("chat.tryExamples")}</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleExampleClick(t("chat.example1"))}
              className="px-3 py-1.5 text-xs rounded-full bg-gray-100 dark:bg-gray-800 
                         text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {t("chat.example1")}
            </button>
            <button
              onClick={() => handleExampleClick(t("chat.example2"))}
              className="px-3 py-1.5 text-xs rounded-full bg-gray-100 dark:bg-gray-800 
                         text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {t("chat.example2")}
            </button>
            <button
              onClick={() => handleExampleClick(t("chat.example3"))}
              className="px-3 py-1.5 text-xs rounded-full bg-gray-100 dark:bg-gray-800 
                         text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {t("chat.example3")}
            </button>
            <button
              onClick={() => handleExampleClick(t("chat.example4"))}
              className="px-3 py-1.5 text-xs rounded-full bg-gray-100 dark:bg-gray-800 
                         text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {t("chat.example4")}
            </button>
          </div>
        </div>

        {/* Input area */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <form onSubmit={handleSubmit} className="flex items-stretch gap-2">
            <div className="flex-1">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("chat.placeholder")}
                className="w-full h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2
                          focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent
                          bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                          resize-none min-h-[44px] max-h-[150px] transition-all"
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                disabled={isTyping}
              />
            </div>
            {isTyping ? (
              <button type="button" onClick={handleStop} className="h-[44px] w-[44px] flex items-center justify-center rounded-lg bg-red-500 text-white shadow-md hover:bg-opacity-90 transition-all">
                <Square className="h-5 w-5" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!message.trim() || isTyping}
                className={`h-[44px] w-[44px] flex items-center justify-center rounded-lg ${
                  !message.trim() || isTyping
                    ? "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    : "bg-brand-blue text-white shadow-md hover:bg-opacity-90 transition-all"
                }`}
              >
                <Send className="h-5 w-5" />
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};
